import React, { useState } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import {
    formatAuditValue,
    getDiffRows,
    isOpaqueValue,
    isStructuredValue,
    structureToLines,
} from './helpers';

import useActivityLogFieldQuery from 'hooks/activity-logs/useActivityLogFieldQuery';

import { ActivityLogChange, ActivtyLog } from 'models/activty-log.model';

import {
    AuditRefs,
    formatListValue,
    resolveAuditValue,
    visibleSnapshotFields,
} from './references';

type Row = Record<string, unknown> | null | undefined;

/** Where a change's full values can be fetched from (for sha256: digests). */
export type ChangeSource = { activityId: number; firstActivityId: number };

type LineChange = 'added' | 'removed' | 'unchanged';

const MARKERS: Record<LineChange, string> = {
    added: '+',
    removed: '−',
    unchanged: '',
};

/** Before-order lines marked kept or removed, then the lines that were added. */
export const diffLines = (
    before: string[],
    after: string[],
): { line: string; change: LineChange }[] => {
    const remaining = [...after];
    const result: { line: string; change: LineChange }[] = before.map(
        (line) => {
            const at = remaining.indexOf(line);
            if (at === -1) return { line, change: 'removed' };
            remaining.splice(at, 1);
            return { line, change: 'unchanged' };
        },
    );
    return [
        ...result,
        ...remaining.map((line) => ({ line, change: 'added' as const })),
    ];
};

/** "max_messages" → "Max messages"; "exit_event_id" → "Exit event". */
export const fieldLabel = (field: string) => {
    // Ids are shown by name where the backend labels them, so drop "id".
    const words = field.replace(/_id$/, '').replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

const AuditValue: React.FC<{ value: unknown }> = ({ value }) =>
    isStructuredValue(value) ? (
        <ValueBlock>{formatAuditValue(value)}</ValueBlock>
    ) : (
        <span>{formatAuditValue(value)}</span>
    );

type ValueProps = {
    objectType: ActivtyLog['object_type'];
    field: string;
    value: unknown;
    row: Row;
    refs: AuditRefs;
};

/** One value: a list as lines, otherwise resolved (ids → names) and formatted. */
export const ChangeValue: React.FC<ValueProps> = ({
    objectType,
    field,
    value,
    row,
    refs,
}) => {
    const lines = formatListValue(objectType, field, value, refs);
    if (!lines) {
        return (
            <AuditValue
                value={resolveAuditValue(objectType, field, value, row, refs)}
            />
        );
    }
    if (lines.length === 0) return <span>None</span>;
    return (
        <ValueList>
            {lines.map((line, index) => (
                <li key={`${index}-${line}`}>{line}</li>
            ))}
        </ValueList>
    );
};

const LineDiff: React.FC<{ before: string[]; after: string[] }> = ({
    before,
    after,
}) => (
    <ListDiff>
        {diffLines(before, after).map(({ line, change: kind }, index) => (
            <ListDiffLine
                key={`${index}-${kind}-${line}`}
                data-change={kind}
                $kind={kind}
            >
                <Marker aria-hidden>{MARKERS[kind]}</Marker>
                <span>{line}</span>
            </ListDiffLine>
        ))}
    </ListDiff>
);

/** A large field stored as a digest: fetched only when someone asks to see it. */
const OpaqueChange: React.FC<{ source: ChangeSource; field: string }> = ({
    source,
    field,
}) => {
    const [requested, setRequested] = useState(false);
    const { data, isLoading, isError } = useActivityLogFieldQuery(
        source,
        field,
        requested,
    );

    if (!requested) {
        return (
            <LinkButton type="button" onClick={() => setRequested(true)}>
                Show change
            </LinkButton>
        );
    }
    if (isLoading) return <Muted>Loading…</Muted>;
    if (isError || !data) return <Muted>Couldn&apos;t load this change.</Muted>;
    if (!data.before.available || !data.after.available) {
        return <Muted>Changed (the full value isn’t available)</Muted>;
    }
    return (
        <LineDiff
            before={structureToLines(data.before.value ?? [])}
            after={structureToLines(data.after.value ?? [])}
        />
    );
};

type AuditChangeTableProps = {
    objectType: ActivtyLog['object_type'];
    change: ActivityLogChange;
    refs: AuditRefs;
    source: ChangeSource;
};

/** The field-by-field table for one object's change (or its snapshot). */
const AuditChangeTable: React.FC<AuditChangeTableProps> = ({
    objectType,
    change,
    refs,
    source,
}) => {
    const diff = getDiffRows(change);
    const snapshotRow = change.after ?? change.before;
    const visible = new Set(
        visibleSnapshotFields(
            diff.rows.map((row) => row.field),
            snapshotRow,
        ),
    );
    const rows = diff.rows.filter(
        (row) => diff.mode === 'diff' || visible.has(row.field),
    );
    if (rows.length === 0) return null;

    const value = (field: string, v: unknown, row: Row) => (
        <ChangeValue
            objectType={objectType}
            field={field}
            value={v}
            row={row}
            refs={refs}
        />
    );

    return (
        <DiffTable>
            {rows.map((row) => {
                const opaque =
                    isOpaqueValue(row.before) ||
                    isOpaqueValue(row.after) ||
                    isOpaqueValue(row.value);
                // Lists (filters, rules, events) read better as added/removed
                // lines than as two whole lists either side of an arrow.
                const was =
                    diff.mode === 'diff'
                        ? formatListValue(
                              objectType,
                              row.field,
                              row.before ?? [],
                              refs,
                          )
                        : undefined;
                const now =
                    diff.mode === 'diff'
                        ? formatListValue(
                              objectType,
                              row.field,
                              row.after ?? [],
                              refs,
                          )
                        : undefined;
                return (
                    <React.Fragment key={row.field}>
                        <Field>{fieldLabel(row.field)}</Field>
                        {opaque ? (
                            <OpaqueChange source={source} field={row.field} />
                        ) : was && now ? (
                            <LineDiff before={was} after={now} />
                        ) : diff.mode === 'diff' ? (
                            <DiffValues>
                                <Before>
                                    {value(
                                        row.field,
                                        row.before,
                                        change.before,
                                    )}
                                </Before>
                                <Arrow aria-hidden>→</Arrow>
                                <After>
                                    {value(row.field, row.after, change.after)}
                                </After>
                            </DiffValues>
                        ) : (
                            <DiffValues>
                                {value(row.field, row.value, snapshotRow)}
                            </DiffValues>
                        )}
                    </React.Fragment>
                );
            })}
        </DiffTable>
    );
};

export default AuditChangeTable;

export const Field = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
});

// Struck through only for plain values; a crossed-out list is hard to read.
export const Before = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
    '& > span': {
        textDecoration: 'line-through',
        textDecorationColor: aampeTheme.colors.solidWG01,
    },
});

export const After = styled.span({
    color: aampeTheme.colors.textAccessibleWG02,
    fontWeight: aampeTheme.weights.medium,
});

export const Arrow = styled.span({
    color: aampeTheme.colors.solidWG01,
});

const DiffTable = styled.div({
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, max-content) minmax(0, 1fr)',
    columnGap: aampeTheme.spacing4(4),
    rowGap: aampeTheme.spacing4(1.5),
    padding: aampeTheme.spacing4(2, 3),
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.backgroundWG01,
    fontSize: aampeTheme.size.font.textMedium,
});

const DiffValues = styled.span({
    display: 'flex',
    alignItems: 'flex-start',
    gap: aampeTheme.spacing4(2),
    flexWrap: 'wrap',
    color: aampeTheme.colors.textAccessibleWG02,
});

const ValueBlock = styled.pre({
    margin: 0,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    fontSize: aampeTheme.size.font.textMedium,
});

const ValueList = styled.ul({
    margin: 0,
    paddingLeft: aampeTheme.spacing4(4),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(0.5),
});

const ListDiff = styled.ul({
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(0.5),
});

const LINE_INK: Record<LineChange, string> = {
    added: aampeTheme.colors.textAccessibleSG01,
    removed: aampeTheme.colors.textAccessibleWG01,
    unchanged: aampeTheme.colors.textAccessibleWG01,
};

const ListDiffLine = styled.li<{ $kind: LineChange }>(({ $kind }) => ({
    display: 'grid',
    gridTemplateColumns: '12px minmax(0, 1fr)',
    columnGap: aampeTheme.spacing4(1.5),
    color: LINE_INK[$kind],
    fontWeight:
        $kind === 'added'
            ? aampeTheme.weights.medium
            : aampeTheme.weights.light,
    '& > span:last-child':
        $kind === 'removed'
            ? {
                  textDecoration: 'line-through',
                  textDecorationColor: aampeTheme.colors.solidWG01,
              }
            : {},
}));

const Marker = styled.span({
    textAlign: 'center',
});

const Muted = styled.span({
    color: aampeTheme.colors.textAccessibleWG01,
});

const LinkButton = styled.button({
    alignSelf: 'flex-start',
    justifySelf: 'start',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: 'inherit',
    color: aampeTheme.colors.solidA01,
    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA01}`,
        outlineOffset: '2px',
    },
});
